import { ArrowRight, Handshake, Users, CalendarDays, Sprout } from "lucide-react";

export const GREEN = "#5E9420";
const HERO = "https://images.pexels.com/photos/29006128/pexels-photo-29006128.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

export const AgapHero = () => (
  <section className="relative bg-[#1F2328] text-white overflow-hidden" data-testid="agap-hero">
    <img src={HERO} alt="African farmer packing harvested rice" className="absolute inset-0 w-full h-full object-cover opacity-35" />
    <div className="relative max-w-7xl mx-auto px-5 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-end">
      <div className="lg:col-span-8 agap-rise">
        <p className="overline text-[#A6D26B]">A subsidiary of Africa One Group</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-4 leading-[1.05]">Africa Global Agribusiness Partnership</h1>
        <p className="mt-6 text-white/80 max-w-2xl leading-relaxed text-base sm:text-lg">
          Connecting African agribusiness to global buyers, investors, policymakers and trade partners, through value-chain consulting,
          the AGAA membership association and curated trade and investment events.
        </p>
        <div className="flex flex-wrap gap-3 mt-8">
          <a href="#agap-register" data-testid="agap-hero-register-btn" className="inline-flex items-center gap-2 bg-[#5E9420] hover:bg-[#4C7A18] px-6 py-3 text-sm font-semibold transition-colors">Register as a partner <ArrowRight size={16} /></a>
          <a href="#agap-register" data-testid="agap-hero-free-btn" onClick={() => window.dispatchEvent(new CustomEvent("agap-pick", { detail: { code: "FREE" } }))} className="border border-white/40 hover:bg-white/10 px-6 py-3 text-sm transition-colors">Get free intelligence access</a>
        </div>
      </div>
      <div className="lg:col-span-4 bg-white p-6 border-t-4 border-[#5E9420] agap-rise [animation-delay:150ms]">
        <img src="/agap-logo.jpg" alt="AGAP logo" className="w-56 h-56 object-contain mx-auto" data-testid="agap-logo" />
        <p className="overline text-center text-[#2F343B] mt-2">Grow Africa. Brand Africa. Trade Africa.</p>
      </div>
    </div>
  </section>
);

const PILLARS = [
  { icon: Handshake, t: "Value-chain consulting", d: "Client referral, brokerage, market access advisory and investment facilitation from origin to shelf." },
  { icon: Users, t: "AGAA membership", d: "The Africa Global Agribusiness Association: a structured way for individuals, companies and countries to join, access opportunities and gain visibility." },
  { icon: CalendarDays, t: "Events & convenings", d: "Origin Cup Africa, Africa Coffee Festival London, the AGAP Annual Conference and the Deal Makers Dinner." },
];

export const AgapAbout = () => (
  <section className="max-w-7xl mx-auto px-5 py-16 lg:py-24 grid lg:grid-cols-12 gap-12" data-testid="agap-about">
    <div className="lg:col-span-5">
      <p className="overline" style={{ color: GREEN }}>Our purpose</p>
      <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3 leading-tight">Putting the right people in the room</h2>
      <p className="text-[#575757] mt-5 leading-relaxed">
        AGAP unlocks the value of African agriculture by bringing producers, exporters, buyers, investors and governments together, and turning those
        connections into trade, investment and long-term partnerships.
      </p>
      <div className="mt-8 border-l-4 pl-5 space-y-3 text-sm" style={{ borderColor: GREEN }}>
        <p><b>Vision.</b> An African agribusiness sector that trades on equal terms with the world, capturing more value at origin.</p>
        <p><b>Mission.</b> To build trusted bridges between African origin markets and global demand.</p>
        <p><b>Founders.</b> Rachael White and Wilbert Chaniwa.</p>
      </div>
    </div>
    <div className="lg:col-span-7 grid gap-px bg-[#E7E7E7] border border-[#E7E7E7]">
      {PILLARS.map(({ icon: I, t, d }, i) => (
        <div key={t} className="bg-white p-6 sm:p-8 flex gap-5 hover:bg-[#F6FAF0] transition-colors" data-testid={`agap-pillar-${i + 1}`}>
          <span className="overline text-[#A0A0A0] pt-1">0{i + 1}</span>
          <div>
            <p className="font-display text-xl font-bold flex items-center gap-2"><I size={18} style={{ color: GREEN }} /> {t}</p>
            <p className="text-sm text-[#575757] mt-2 leading-relaxed">{d}</p>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const CALENDAR = [
  ["Origin Cup · Ethiopia", "June 2026, London", "Delivered"], ["Origin Cup · Rwanda", "31 July 2026, London", "Delivered · Rwanda Embassy endorsed"],
  ["Origin Cup · Kenya", "25 Sept 2026, London", "Delivered · Kenya High Commission endorsed"], ["Origin Cup · Uganda", "30 Oct 2026, London", "Delivered"],
  ["Origin Cup · Ghana", "27 Nov 2026, London", "Upcoming · Cocoa & Coffee"], ["Africa Coffee Festival London", "August 2027, London", "Upcoming · Flagship festival"],
  ["AGAP Annual Conference & Deal Makers Dinner", "To be announced", "Announced via Africa Brew Brief"],
];
const WHY = [["Qualified access", "Curated rooms of buyers, investors, exporters and policymakers."], ["Institutional credibility", "Embassy and high-commission endorsement on country editions."],
  ["Media reach", "Year-round promotion through Africa Brew Brief's editorial and social channels."], ["Group backing", "Linked into Africa One Group's trade and investment businesses."],
  ["Multi-platform presence", "One relationship across consulting, membership and multiple events."]];

export const AgapTrack = () => (
  <section className="bg-[#F6FAF0] border-y border-[#E3EDD5]" data-testid="agap-track">
    <div className="max-w-7xl mx-auto px-5 py-16 lg:py-20 grid lg:grid-cols-2 gap-14">
      <div>
        <p className="overline" style={{ color: GREEN }}>Track record & calendar</p>
        <h2 className="font-display text-2xl sm:text-3xl font-bold mt-3">Five editions in, and growing</h2>
        <ul className="mt-6 divide-y divide-[#DCE7CC]">
          {CALENDAR.map(([e, d, n]) => (
            <li key={e} className="py-3 grid sm:grid-cols-[1.4fr_1fr] gap-1 text-sm" data-testid="agap-calendar-row">
              <span className="font-semibold">{e}</span>
              <span className="text-[#575757]">{d}<span className={`block text-xs ${n.startsWith("Upcoming") ? "text-[#4C7A18] font-semibold" : "text-[#8A8A8A]"}`}>{n}</span></span>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="overline" style={{ color: GREEN }}>Why partner with AGAP</p>
        <h2 className="font-display text-2xl sm:text-3xl font-bold mt-3">One relationship, every room</h2>
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          {WHY.map(([t, d]) => (
            <div key={t} className="bg-white p-5 border border-[#E3EDD5] hover:-translate-y-0.5 transition-transform">
              <Sprout size={16} style={{ color: GREEN }} />
              <p className="font-semibold mt-2">{t}</p>
              <p className="text-sm text-[#575757] mt-1">{d}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-[#575757] mt-6">AGAA members receive 15–20% off all AGAP event fees. Early-bird rates apply 60+ days before an event; bundled packages on request.</p>
      </div>
    </div>
  </section>
);
